import React, { useState } from 'react';
import { userService } from '../services/api';
import { Calendar, Briefcase, Target, User, ChevronRight } from 'lucide-react';
import './InitialSetup.css';
// import birthDateAudio from '../assets/initial-setup-audio/birth_date.mp3';
// import educationDegreeAudio from '../assets/initial-setup-audio/education_degree.mp3';
// import currentStatusAudio from '../assets/initial-setup-audio/current_status.mp3';
// import motivationAudio from '../assets/initial-setup-audio/motivation.mp3';

const steps = [
  'BirthDate',
  'EducationDegree',
  'CurrentStatus',
  'Motivation'
];

export default function InitialSetup() {
  const [stepIndex, setStepIndex] = useState(0);
  const [payload, setPayload] = useState({});
  const [saving, setSaving] = useState(false);
  const [canProceed, setCanProceed] = useState(false);
  const [ageError, setAgeError] = useState('');

  // Prefill from server so returning users see their previous data
  React.useEffect(() => {
    (async () => {
      try {
        const me = await userService.getUser();
        const fmtDate = (d) => {
          if (!d) return '';
          try {
            const dt = new Date(d);
            if (!isNaN(dt.getTime())) {
              const yyyy = dt.getFullYear();
              const mm = String(dt.getMonth() + 1).padStart(2, '0');
              const dd = String(dt.getDate()).padStart(2, '0');
              return `${yyyy}-${mm}-${dd}`;
            }
            if (typeof d === 'string') return d.substring(0, 10);
          } catch (_) {}
          return '';
        };
        setPayload((prev) => ({
          ...prev,
          birthDate: fmtDate(me.birthDate),
          currentStatus: me.currentStatus ?? '',
          motivation: me.motivation ?? '',
          interests: me.interests || '',
          aboutYourself: me.aboutYourself || '',
          educationDegree: me.educationDegree || '',
          learningPreferences: typeof me.learningPreferences === 'number' ? me.learningPreferences : 0,
          learningSchedule: Array.isArray(me.learningSchedule) && me.learningSchedule.length > 0
            ? me.learningSchedule.map(s => ({
                dayOfWeek: s.dayOfWeek ?? s.DayOfWeek ?? '',
                startTime: s.startTime ?? s.StartTime ?? '18:00:00',
                endTime: s.endTime ?? s.EndTime ?? '19:00:00'
              }))
            : []
        }));
      } catch (_) { /* ignore */ }
    })();
  }, []);

  // Restore last visited step from localStorage
  React.useEffect(() => {
    try {
      const lastStep = localStorage.getItem('initialSetupLastStep');
      if (lastStep) {
        const idx = steps.indexOf(lastStep);
        if (idx >= 0) {
          setStepIndex(idx);
        }
      }
    } catch (_) { /* ignore storage errors */ }
  }, []);

  // Calculate age and validate birth date
  const validateAge = (birthDate) => {
    if (!birthDate) return { isValid: false, error: '' };
    
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    // Adjust age if birthday hasn't occurred this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    if (age < 13) {
      return { isValid: false, error: 'Unfortunately, users younger than 13 years old cannot be registered.' };
    } else if (age > 100) {
      return { isValid: false, error: 'Birth date seems not real. Please check your birth date.' };
    }
    
    return { isValid: true, error: '' };
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const goNext = async () => {
    const step = steps[stepIndex];
    
    // Validate age for birth date step
    if (step === 'BirthDate') {
      const ageValidation = validateAge(payload.birthDate);
      if (!ageValidation.isValid) {
        setAgeError(ageValidation.error);
        return; // Don't proceed to next step
      }
      setAgeError(''); // Clear any previous errors
    }
    
    // Build minimal patch for this step
    const buildPatch = () => {
      const normalizeTime = (t) => {
        if (!t) return '18:00:00';
        // Accept HH:mm or HH:mm:ss
        if (/^\d{2}:\d{2}$/.test(t)) return `${t}:00`;
        return t;
      };
      switch (step) {
        case 'BirthDate':
          return { birthDate: payload.birthDate || null };
        case 'EducationDegree':
          return { educationDegree: payload.educationDegree || '' };
        case 'CurrentStatus':
          return { currentStatus: payload.currentStatus };
        case 'Motivation':
          return { motivation: payload.motivation };
        case 'Interests':
          return { interests: payload.interests || '' };
        case 'AboutYourself':
          return { aboutYourself: payload.aboutYourself || '' };
        case 'LearningPreferences':
          return { learningPreferences: payload.learningPreferences ?? 0 };
        case 'Schedule':
          return {
            learningSchedule: (payload.learningSchedule || [])
              .filter(s => s.startTime && s.endTime)
              .map(s => ({
              dayOfWeek: s.dayOfWeek,
              startTime: normalizeTime(s.startTime),
              endTime: normalizeTime(s.endTime)
            }))
          };
        default:
          return {};
      }
    };

    setSaving(true);
    try {
      // Persist the current step's data
      await userService.updateProfile(buildPatch());

      // If final step, finish setup
      if (stepIndex >= steps.length - 1) {
        await userService.updateProfile({ isInitialSetupCompleted: true });
        try { localStorage.removeItem('initialSetupLastStep'); } catch (_) {}
        window.location.href = '/goals';
        return;
      }

      // Move to next step if not final
      setStepIndex(stepIndex + 1);
    } finally {
      setSaving(false);
    }
  };

  const step = steps[stepIndex];

  // Persist last visited step whenever it changes
  React.useEffect(() => {
    try {
      localStorage.setItem('initialSetupLastStep', steps[stepIndex]);
    } catch (_) { /* ignore */ }
  }, [stepIndex]);

  // Voice command: advance to next step via event dispatched by Realtime tool call
  React.useEffect(() => {
    const handler = async () => {
      // Mirror button behavior: only proceed if allowed and not saving
      if (!canProceed || saving) return;
      try {
        await goNext();
      } catch (err) {
        console.warn('Failed to advance to next setup step via voice:', err);
      }
    };
    window.addEventListener('oai-next-step', handler);
    return () => window.removeEventListener('oai-next-step', handler);
  }, [stepIndex, payload, canProceed, saving]);

  // Audio playback disabled on initial setup page per request.

  // Enable Next only when current step has value
  React.useEffect(() => {
    switch (step) {
      case 'BirthDate':
        const ageValidation = validateAge(payload.birthDate);
        setCanProceed(Boolean(payload.birthDate) && ageValidation.isValid);
        break;
      case 'EducationDegree':
        setCanProceed(true); // optional
        break;
      case 'CurrentStatus':
        setCanProceed(payload.currentStatus !== undefined && payload.currentStatus !== null && payload.currentStatus !== '');
        break;
      case 'Motivation':
        setCanProceed(payload.motivation !== undefined && payload.motivation !== null && payload.motivation !== '');
        break;
      default:
        setCanProceed(false);
    }
  }, [step, payload]);

  // Handle assistant tool calls to fill/modify fields
  React.useEffect(() => {
    const dispatchToolResult = (name, ok, message) => {
      try {
        window.dispatchEvent(new CustomEvent('oai-tool-result', { detail: { name, ok, message } }));
      } catch (_) {}
    };

    const onSetBirthdate = (e) => {
      let raw = e.detail?.isoDate ?? e.detail?.date ?? e.detail;
      // If we received a JSON string, parse it
      if (typeof raw === 'string' && raw.trim().startsWith('{')) {
        try {
          const obj = JSON.parse(raw);
          if (obj && typeof obj.isoDate === 'string') raw = obj.isoDate;
        } catch (_) { /* ignore */ }
      }
      let iso = typeof raw === 'string' ? raw.trim() : '';
      // If not already ISO, try to extract and normalize natural language date
      if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        if (typeof raw === 'string') {
          let txt = raw
            .replace(/\b(\d{1,2})(st|nd|rd|th)\b/gi, '$1')
            .replace(/\bof\b/gi, ' ')
            .replace(/[.,]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          const d = new Date(txt);
          if (!isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            iso = `${yyyy}-${mm}-${dd}`;
          } else if (/^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/.test(txt)) {
            const parts = txt.split(/[-\/]/).map(p => parseInt(p, 10));
            let [a,b,c] = parts;
            let day, month, year;
            // Heuristic: if first part > 12 -> DD/MM/YYYY, else MM/DD/YYYY assuming US
            if (a > 12) { day = a; month = b; year = c; } else { month = a; day = b; year = c; }
            if (year < 100) year += 2000;
            const d2 = new Date(year, month - 1, day);
            if (!isNaN(d2.getTime())) {
              const yyyy = d2.getFullYear();
              const mm = String(d2.getMonth() + 1).padStart(2, '0');
              const dd = String(d2.getDate()).padStart(2, '0');
              iso = `${yyyy}-${mm}-${dd}`;
            }
          }
        }
      }
      const v = validateAge(iso);
      if (!iso || !v.isValid) {
        // Do not show generic "Invalid date" in UI; only show specific age errors
        if (v.error) setAgeError(v.error); else setAgeError('');
        dispatchToolResult('set_birthdate', false, v.error || 'Invalid date.');
        return;
      }
      setAgeError('');
      setPayload(prev => ({ ...prev, birthDate: iso }));
      dispatchToolResult('set_birthdate', true, 'Birth date updated.');
    };

    const onSetEducationDegree = (e) => {
      const raw = e.detail?.degree ?? e.detail;
      const normalize = (s) => String(s || '').trim().toLowerCase();
      const mapDegree = (input) => {
        const s = normalize(input).replace(/[^a-z\s]/g, '').replace(/\s+/g, ' ');
        if (!s) return null;
        if (/(^|\b)(high school|secondary|hs)(\b|$)/.test(s)) return 'HighSchool';
        if (/(^|\b)(associate|associates|associate degree)(\b|$)/.test(s)) return 'Associate';
        if (/(^|\b)(bachelor|bachelors|bsc|bs|ba|undergrad|undergraduate)(\b|$)/.test(s)) return 'Bachelor';
        if (/(^|\b)(master|masters|msc|ms|ma|graduate)(\b|$)/.test(s)) return 'Master';
        if (/(^|\b)(phd|ph\.d|doctorate|doctoral|doctor of philosophy|dphil)(\b|$)/.test(s)) return 'PhD';
        if (/(^|\b)(other|none|no degree)(\b|$)/.test(s)) return 'Other';
        return null;
      };
      let degree = mapDegree(raw);
      if (!degree) {
        // Accept exact canonical value if model already sent it
        const canonical = String(raw || '').trim();
        const allowed = new Set(['HighSchool','Associate','Bachelor','Master','PhD','Other']);
        if (allowed.has(canonical)) {
          degree = canonical;
        }
      }
      if (!degree) {
        dispatchToolResult('set_education_degree', false, 'Could not recognize education degree. Please say High School, Associate, Bachelor, Master, or PhD.');
        return;
      }
      setPayload(prev => ({ ...prev, educationDegree: degree }));
      dispatchToolResult('set_education_degree', true, 'Education degree updated.');
    };

    const onSetCurrentStatus = (e) => {
      let val = e.detail?.status ?? e.detail;
      if (typeof val === 'string' && val.trim().startsWith('{')) {
        try { const obj = JSON.parse(val); if (obj && obj.status) val = obj.status; } catch (_) {}
      }
      const normalize = (s) => String(s || '').toLowerCase().replace(/[^a-z\s-]/g,' ').replace(/\s+/g,' ').trim();
      const mapStatus = (input) => {
        if (typeof input === 'number') return [0,1,2].includes(input) ? input : '';
        const s = normalize(input);
        if (!s) return '';
        if (s.includes('unemploy') || s.includes('not working') || s.includes('no job') || s.includes('jobless') || s.includes('between job')) return 0;
        if (s.includes('student') || s.includes('study') || s.includes('school') || s.includes('college') || s.includes('university') || s.includes('pupil')) return 1;
        if (s.includes('employ') || s.includes('work') || s.includes('full time') || s.includes('full-time') || s.includes('fulltime') || s.includes('self employ') || s.includes('freelance') || s.includes('contract')) return 2;
        if (s.includes('part time') || s.includes('part-time') || s.includes('parttime')) return 2; // treat as employed
        if (s === 'unemployed') return 0;
        if (s === 'student') return 1;
        if (s === 'employed full time' || s === 'employed_full_time' || s === 'employed') return 2;
        return '';
      };
      const status = mapStatus(val);
      if (status === '') {
        dispatchToolResult('set_current_status', false, 'Could not recognize status. Please say unemployed, student, or employed full time.');
        return;
      }
      setPayload(prev => ({ ...prev, currentStatus: status }));
      dispatchToolResult('set_current_status', true, 'Status updated.');
    };

    const onSetMotivation = (e) => {
      const val = String(e.detail?.motivation ?? e.detail ?? '').toLowerCase().replace(/\s+/g,'_');
      const map = { career_growth:0, curiosity:1, change_career:2, change_life:3, other:4 };
      const m = (val in map) ? map[val] : '';
      setPayload(prev => ({ ...prev, motivation: m }));
      dispatchToolResult('set_motivation', m !== '', m !== '' ? 'Motivation updated.' : 'Unknown motivation.');
    };





    window.addEventListener('oai-set-birthdate', onSetBirthdate);
    window.addEventListener('oai-set-education-degree', onSetEducationDegree);
    window.addEventListener('oai-set-current-status', onSetCurrentStatus);
    window.addEventListener('oai-set-motivation', onSetMotivation);

    return () => {
      window.removeEventListener('oai-set-birthdate', onSetBirthdate);
      window.removeEventListener('oai-set-education-degree', onSetEducationDegree);
      window.removeEventListener('oai-set-current-status', onSetCurrentStatus);
      window.removeEventListener('oai-set-motivation', onSetMotivation);
    };
  }, [validateAge]);

  return (
    <div className="initial-setup">
      <div className="branding">
        <span className="brand-name">Brainy</span>
      </div>
      <div className="card">
        <h2>Let’s personalize your experience</h2>
        {step === 'BirthDate' && (
          <div className="setup-field">
            <label className="label-with-icon"><Calendar size={16} />When is your birth date?</label>
            <input
              type="date"
              value={payload.birthDate || ''}
              onChange={(e) => {
                setPayload({ ...payload, birthDate: e.target.value });
                setAgeError(''); // Clear error when user changes date
              }}
              className="date-picker"
              max={new Date().toISOString().substring(0, 10)}
            />
            {ageError && (
              <div className="error-message">
                {ageError}
              </div>
            )}
          </div>
        )}
        {step === 'EducationDegree' && (
          <div className="setup-field">
            <label className="label-with-icon"><User size={16} />Your education degree</label>
            <select
              autoFocus
              value={payload.educationDegree ?? ''}
              onChange={(e)=>setPayload({ ...payload, educationDegree: e.target.value })}
            >
              <option value="">Select degree…</option>
              <option value="HighSchool">High School</option>
              <option value="Associate">Associate</option>
              <option value="Bachelor">Bachelor</option>
              <option value="Master">Master</option>
              <option value="PhD">PhD</option>
              <option value="Other">Other</option>
            </select>
          </div>
        )}
        {step === 'CurrentStatus' && (
          <div className="setup-field">
            <label className="label-with-icon"><Briefcase size={16} />Your current status</label>
            <select autoFocus value={payload.currentStatus ?? ''} onChange={(e)=>setPayload({ ...payload, currentStatus: e.target.value === '' ? '' : parseInt(e.target.value,10) })}>
              <option value="" disabled>Choose…</option>
              <option value={0}>Unemployed</option>
              <option value={1}>Student</option>
              <option value={2}>Employed full-time</option>
            </select>
          </div>
        )}
        {step === 'Motivation' && (
          <div className="setup-field">
            <label className="label-with-icon"><Target size={16} />What best describes your motivation?</label>
            <select autoFocus value={payload.motivation ?? ''} onChange={(e)=>setPayload({ ...payload, motivation: e.target.value === '' ? '' : parseInt(e.target.value,10) })}>
              <option value="" disabled>Choose…</option>
              <option value={0}>Career growth</option>
              <option value={1}>Curiosity</option>
              <option value={2}>Change career</option>
              <option value={3}>Change life</option>
              <option value={4}>Other</option>
            </select>
          </div>
        )}
        <div className="nav-buttons">
          {stepIndex > 0 && (
            <button type="button" className="back" onClick={goBack}>
              Back
            </button>
          )}
          <button className="next" disabled={saving || !canProceed} onClick={goNext}>
            {stepIndex < steps.length - 1 ? (<><span>Next</span> <ChevronRight size={16} /></>) : 'Finish'}
          </button>
        </div>
      </div>
    </div>
  );
}


